import { getConsoleAuthToken } from "@/hooks/use-console-auth-token";
import { imagineConfigStore } from "@/stores/imagine-config.store";

/**
 * Fetch wrapper for AI service JSON API calls.
 * Automatically includes auth token and session ID headers.
 */
export async function aiServiceFetch(
  path: string,
  options?: RequestInit,
  forceTokenRefresh: boolean = false,
): Promise<Response> {
  const config = imagineConfigStore.getState().config;
  const consoleSessionId = imagineConfigStore.getState().consoleSessionId;

  if (!config?.AI_SERVICE_ENDPOINT) {
    throw new Error("AI service endpoint is not configured");
  }

  const token = await getConsoleAuthToken({ forceRefresh: forceTokenRefresh });
  const url = `${config.AI_SERVICE_ENDPOINT}${path}`;

  return fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "X-Imagine-Token": token,
      "X-Imagine-Session-Id": consoleSessionId,
      ...options?.headers,
    },
  });
}

/**
 * Fetch wrapper for AI service file uploads (FormData).
 * Omits Content-Type to let browser set multipart boundary automatically.
 */
export async function aiServiceUpload(
  path: string,
  formData: FormData,
  options?: Omit<RequestInit, "body" | "method">,
): Promise<Response> {
  const config = imagineConfigStore.getState().config;
  const consoleSessionId = imagineConfigStore.getState().consoleSessionId;

  if (!config?.AI_SERVICE_ENDPOINT) {
    throw new Error("AI service endpoint is not configured");
  }

  const token = await getConsoleAuthToken();
  const url = `${config.AI_SERVICE_ENDPOINT}${path}`;

  return fetch(url, {
    method: "POST",
    body: formData,
    ...options,
    headers: {
      // No Content-Type - browser sets multipart/form-data with boundary
      "X-Imagine-Token": token,
      "X-Imagine-Session-Id": consoleSessionId,
      ...options?.headers,
    },
  });
}
